<template>
  <div class="user-detail-page">
    <!-- Toolbar -->
    <div class="panel-toolbar">
      <router-link to="/panel/usuarios" class="btn btn-outline-light btn-sm">
        <i class="fas fa-arrow-left me-2"></i>Volver a usuarios
      </router-link>
      <div class="toolbar-right" v-if="user">
        <button v-if="user.email" type="button" class="btn btn-outline-primary btn-sm me-2" @click="contactModal?.openForCustomer(user, orders)">
          <i class="fas fa-envelope me-1"></i>Contactar
        </button>
        <button class="btn btn-primary btn-sm" :disabled="saving" @click="saveChanges">
          <i class="fas fa-save me-1"></i>
          <span v-if="saving">Guardando…</span>
          <span v-else>Guardar cambios</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-3 text-muted">Cargando usuario…</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="state-box state-error">
      <i class="fas fa-exclamation-circle fa-2x mb-3"></i>
      <p>{{ error }}</p>
      <button class="btn btn-outline-light btn-sm mt-2" @click="loadUser">Reintentar</button>
    </div>

    <div v-else-if="user" class="detail-grid">
      <!-- Columna izquierda: perfil -->
      <div class="col-profile">

        <!-- Avatar + nombre -->
        <div class="profile-card">
          <div class="profile-avatar" :class="{ inactive: !user.is_active }">
            {{ userInitials }}
          </div>
          <div class="profile-names">
            <h2 class="profile-name">{{ user.first_name }} {{ user.last_name }}</h2>
            <p class="profile-username">@{{ user.username }}</p>
            <span v-if="user.is_staff" class="role-tag role-admin">Admin</span>
            <span v-else class="role-tag" :style="{ backgroundColor: user.role_color || '#6c757d' }">
              {{ user.role_display || 'Cliente' }}
            </span>
          </div>
          <div class="profile-status">
            <span
              class="status-badge"
              :class="user.email_verified ? 'status-ok' : 'status-warn'"
            >
              <i :class="user.email_verified ? 'fas fa-check-circle' : 'fas fa-envelope'" class="me-1"></i>
              {{ user.email_verified ? 'Email verificado' : 'Email pendiente' }}
            </span>
            <span
              class="status-badge ms-2"
              :class="user.has_recent_purchase ? 'status-ok' : 'status-muted'"
            >
              <i :class="user.has_recent_purchase ? 'fas fa-shopping-bag' : 'fas fa-moon'" class="me-1"></i>
              {{ user.has_recent_purchase ? 'Activo' : 'Inactivo' }}
            </span>
          </div>
        </div>

        <!-- Sección: Datos personales (readonly) -->
        <div class="info-card">
          <h3 class="info-card-title"><i class="fas fa-user me-2"></i>Datos personales</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Email</span>
              <span class="info-value">{{ user.email }}</span>
            </div>
            <div class="info-item" v-if="user.phone_number">
              <span class="info-label">Teléfono</span>
              <span class="info-value">{{ user.phone_number }}</span>
            </div>
            <div class="info-item" v-if="user.dni">
              <span class="info-label">DNI</span>
              <span class="info-value">{{ user.dni }}</span>
            </div>
            <div class="info-item" v-if="user.cuit">
              <span class="info-label">CUIT/CUIL</span>
              <span class="info-value">{{ user.cuit }}</span>
            </div>
            <div class="info-item" v-if="user.instagram">
              <span class="info-label">Instagram</span>
              <span class="info-value">{{ user.instagram }}</span>
            </div>
            <div class="info-item" v-if="user.facebook">
              <span class="info-label">Facebook</span>
              <span class="info-value">{{ user.facebook }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Miembro desde</span>
              <span class="info-value">{{ formatDate(user.date_joined) }}</span>
            </div>
          </div>
        </div>

        <!-- Sección: Datos del negocio -->
        <div class="info-card" v-if="user.business_name || user.business_address || user.business_type">
          <h3 class="info-card-title"><i class="fas fa-store me-2"></i>Datos del negocio</h3>
          <div class="info-grid">
            <div class="info-item" v-if="user.business_name">
              <span class="info-label">Nombre</span>
              <span class="info-value">{{ user.business_name }}</span>
            </div>
            <div class="info-item" v-if="user.business_type">
              <span class="info-label">Tipo</span>
              <span class="info-value">{{ user.business_type }}</span>
            </div>
            <div class="info-item" v-if="user.business_address">
              <span class="info-label">Dirección</span>
              <span class="info-value">{{ user.business_address }}</span>
            </div>
          </div>
        </div>

        <!-- Sección: Cambio de rol -->
        <div class="info-card" v-if="!user.is_staff">
          <h3 class="info-card-title"><i class="fas fa-tag me-2"></i>Rol comercial</h3>
          <div class="field-group">
            <label class="field-label">Rol asignado</label>
            <select v-model="editForm.role" class="field-select">
              <option v-for="r in roleOptions" :key="r.slug" :value="r.slug">{{ r.label }}</option>
            </select>
          </div>
          <div class="field-group mt-2">
            <label class="field-label">B2B / Mayorista</label>
            <div class="toggle-wrap">
              <button
                type="button"
                class="toggle-btn"
                :class="editForm.is_approved ? 'toggle-on' : 'toggle-off'"
                @click="editForm.is_approved = !editForm.is_approved"
              >
                <span class="toggle-knob"></span>
              </button>
              <span class="toggle-label">{{ editForm.is_approved ? 'Aprobado' : 'Pendiente' }}</span>
            </div>
          </div>
        </div>

        <!-- Sección: Notas del cliente -->
        <div class="info-card">
          <h3 class="info-card-title"><i class="fas fa-sticky-note me-2"></i>Notas del cliente</h3>
          <p class="info-hint">Notas internas — solo visible para administradores.</p>
          <textarea
            v-model="editForm.extra_info"
            class="notes-textarea"
            rows="5"
            placeholder="Agregar notas sobre este cliente…"
          ></textarea>
        </div>

      </div>

      <!-- Columna derecha: pedidos -->
      <div class="col-orders">
        <div class="orders-card">
          <div class="orders-header">
            <h3 class="info-card-title mb-0"><i class="fas fa-shopping-bag me-2"></i>Pedidos</h3>
            <span class="orders-count">{{ orders.length }}</span>
          </div>

          <div v-if="loadingOrders" class="orders-loading">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="ms-2 text-muted">Cargando pedidos…</span>
          </div>

          <div v-else-if="!orders.length" class="empty-orders">
            <i class="fas fa-shopping-cart fa-2x mb-3 text-muted"></i>
            <p>Sin pedidos registrados</p>
          </div>

          <div v-else class="orders-list">
            <div v-for="order in orders" :key="order.id" class="order-row">
              <div class="order-row-top">
                <router-link :to="`/panel/pedidos/${order.id}`" class="order-id">
                  #{{ order.id }}
                </router-link>
                <span class="order-status" :class="`status-${order.status}`">
                  {{ order.status_display }}
                </span>
                <span class="order-date">{{ formatDate(order.created_at) }}</span>
              </div>
              <div class="order-row-bottom">
                <span class="order-products-count">
                  {{ order.products?.length || 0 }} producto{{ (order.products?.length || 0) !== 1 ? 's' : '' }}
                </span>
                <span class="order-total">{{ formatPrice(order.total) }}</span>
              </div>
            </div>
          </div>

          <div v-if="ordersTotal > 0" class="orders-summary">
            <span>Total gastado</span>
            <strong>{{ formatPrice(ordersTotal) }}</strong>
          </div>
        </div>
      </div>
    </div>
    <CustomerContactModal ref="contactModal" />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import apiClient from '@/services/apiClient'
import CustomerContactModal from '@/components/admin/CustomerContactModal.vue'
import { getCustomerRoles } from '@/services/invitations'
import { useUnsavedChangesGuard, useFormDirty } from '@/composables/useUnsavedChangesGuard'

const route = useRoute()
const toast = useToast()

const user = ref(null)
const contactModal = ref(null)
const orders = ref([])
const roleOptions = ref([])
const loading = ref(true)
const loadingOrders = ref(false)
const saving = ref(false)
const error = ref(null)

const editForm = reactive({
  role: '',
  is_approved: false,
  extra_info: '',
})

const editDirty = useFormDirty(editForm)

useUnsavedChangesGuard({
  dirty: editDirty.dirty,
  saveFn: () => saveChanges(),
  message: 'Tenés cambios sin guardar en el usuario. Si salís ahora, se van a perder.',
})

const userInitials = computed(() => {
  if (!user.value) return '?'
  const f = (user.value.first_name || '').trim()
  const l = (user.value.last_name || '').trim()
  if (f || l) return ((f[0] || '') + (l[0] || '')).toUpperCase()
  return (user.value.username || '?').slice(0, 2).toUpperCase()
})

const ordersTotal = computed(() =>
  orders.value.reduce((sum, o) => sum + parseFloat(o.total || 0), 0)
)

const formatDate = (val) => {
  if (!val) return '—'
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(new Date(val))
}

const formatPrice = (val) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val || 0)

const loadUser = async () => {
  const id = route.params.id
  if (!id) {
    error.value = 'ID de usuario no encontrado'
    loading.value = false
    return
  }
  loading.value = true
  error.value = null
  try {
    const res = await apiClient.get(`/auth/users/${id}/`)
    user.value = res.data
    editForm.role = res.data.role || 'retail'
    editForm.is_approved = !!res.data.is_approved
    editForm.extra_info = res.data.extra_info || ''
    editDirty.markClean()
  } catch (e) {
    error.value = 'No se pudo cargar el usuario. Verificá que exista.'
    toast.error(error.value)
  } finally {
    loading.value = false
  }
}

const loadOrders = async () => {
  const id = route.params.id
  if (!id) return
  loadingOrders.value = true
  try {
    const res = await apiClient.get(`/orders/?user=${id}&page_size=50`)
    orders.value = res.data?.results || res.data || []
  } catch {
    orders.value = []
  } finally {
    loadingOrders.value = false
  }
}

const loadRoles = async () => {
  try {
    const data = await getCustomerRoles({ active_only: true })
    roleOptions.value = Array.isArray(data) ? data : (data.results || [])
  } catch {
    roleOptions.value = []
  }
}

const saveChanges = async () => {
  if (!user.value) return
  saving.value = true
  try {
    const payload = {
      role: editForm.role,
      is_approved: editForm.is_approved,
      extra_info: editForm.extra_info,
    }
    const res = await apiClient.patch(`/auth/users/${user.value.id}/`, payload)
    user.value = { ...user.value, ...res.data }
    toast.success('Cambios guardados correctamente')
    editDirty.markClean()
  } catch (e) {
    const msg = e.response?.data?.detail || e.response?.data?.role?.[0] || 'Error al guardar'
    toast.error(msg)
  } finally {
    saving.value = false
  }
}


onMounted(async () => {
  await Promise.all([loadRoles(), loadUser()])
  loadOrders()
})
</script>

<style scoped>
.user-detail-page {
  padding-bottom: 3rem;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 2rem;
  color: var(--white-color);
}

.detail-grid {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 1.5rem;
  align-items: start;
}

@media (max-width: 991px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}

/* Profile card */
.profile-card {
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.profile-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--primary-color);
  color: var(--black-color);
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-avatar.inactive {
  background: var(--black-color-2);
  color: rgba(255,255,255,0.4);
}

.profile-name {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--white-color);
  margin: 0;
}

.profile-username {
  font-size: 0.88rem;
  color: rgba(255,255,255,0.5);
  margin: 0;
}

.role-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #000;
}

.role-admin {
  background: rgba(255,236,0,0.2) !important;
  color: var(--primary-color) !important;
}

.profile-status {
  margin-top: 0.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
}

.status-ok {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

.status-warn {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.status-muted {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.45);
}

/* Info cards */
.info-card {
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;
}

.info-card-title {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255,255,255,0.5);
  margin-bottom: 1rem;
}

.info-hint {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.35);
  margin-bottom: 0.75rem;
  margin-top: -0.5rem;
}

.info-grid {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}

.info-item:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.info-label {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.45);
  flex-shrink: 0;
}

.info-value {
  font-size: 0.88rem;
  color: var(--white-color);
  text-align: right;
  word-break: break-all;
}

/* Form fields */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.field-label {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.5);
}

.field-select {
  background: rgba(0,0,0,0.3);
  border: 1px solid var(--panel-border);
  border-radius: 10px;
  color: var(--white-color);
  padding: 0.45rem 0.75rem;
  font-size: 0.88rem;
  outline: none;
}

.field-select:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(255,236,0,0.12);
}

.toggle-wrap {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.toggle-btn {
  width: 42px;
  height: 24px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
  padding: 0;
  flex-shrink: 0;
}

.toggle-on { background: #28a745; }
.toggle-off { background: rgba(255,255,255,0.15); }

.toggle-knob {
  position: absolute;
  top: 3px;
  width: 18px;
  height: 18px;
  background: #fff;
  border-radius: 50%;
  transition: left 0.2s;
}

.toggle-on .toggle-knob { left: calc(100% - 21px); }
.toggle-off .toggle-knob { left: 3px; }

.toggle-label {
  font-size: 0.85rem;
  color: rgba(255,255,255,0.7);
}

.notes-textarea {
  width: 100%;
  background: rgba(0,0,0,0.25);
  border: 1px solid var(--panel-border);
  border-radius: 10px;
  color: var(--white-color);
  padding: 0.65rem 0.85rem;
  font-size: 0.88rem;
  resize: vertical;
  outline: none;
  font-family: inherit;
  line-height: 1.6;
}

.notes-textarea:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(255,236,0,0.12);
}

.notes-textarea::placeholder {
  color: rgba(255,255,255,0.3);
}

/* Orders column */
.orders-card {
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  overflow: hidden;
}

.orders-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--panel-border);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.orders-count {
  background: var(--primary-color);
  color: var(--black-color);
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 700;
}

.orders-loading {
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-orders {
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.4);
  text-align: center;
}

.orders-list {
  max-height: 520px;
  overflow-y: auto;
}

.order-row {
  padding: 0.85rem 1.5rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  transition: background 0.12s;
}

.order-row:last-child { border-bottom: none; }
.order-row:hover { background: rgba(255,255,255,0.02); }

.order-row-top {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.3rem;
  flex-wrap: wrap;
}

.order-id {
  font-weight: 700;
  color: var(--primary-color);
  text-decoration: none;
  font-size: 0.9rem;
}

.order-id:hover { text-decoration: underline; }

.order-status {
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
}

.status-pending { background: rgba(255,193,7,0.15); color: #ffc107; }
.status-payment_pending { background: rgba(255,153,0,0.15); color: #ff9900; }
.status-paid { background: rgba(16,185,129,0.15); color: #10b981; }
.status-shipped { background: rgba(99,102,241,0.15); color: #818cf8; }
.status-completed { background: rgba(40,167,69,0.15); color: #28a745; }
.status-cancelled { background: rgba(220,53,69,0.12); color: #dc3545; }

.order-date {
  font-size: 0.78rem;
  color: rgba(255,255,255,0.4);
  margin-left: auto;
}

.order-row-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-products-count {
  font-size: 0.8rem;
  color: rgba(255,255,255,0.4);
}

.order-total {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--white-color);
}

.orders-summary {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--panel-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.88rem;
  color: rgba(255,255,255,0.6);
  background: rgba(0,0,0,0.1);
}

.orders-summary strong {
  color: var(--primary-color);
  font-size: 1rem;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: var(--panel-surface);
  border: 1px solid var(--panel-border);
  border-radius: 16px;
  color: var(--white-color);
}

.state-error {
  border-color: rgba(220,53,69,0.3);
}
</style>
